"use client";

import React, { useState } from "react";
import Link from "next/link";
import { ChevronDown } from "lucide-react";

export interface DrawerMenuNode {
  _id: string;
  title?: { en?: string; mm?: string };
  url?: string;
  slug?: string;
  openInNewTab?: boolean;
  children?: DrawerMenuNode[];
}

function pickLang(
  v: { en?: string; mm?: string } | undefined | null,
  lang: "en" | "mm",
): string {
  if (!v) return "";
  return (lang === "mm" ? v.mm : v.en) || v.en || v.mm || "";
}

/**
 * Client accordion for the mobile drawer menu.
 *
 * Every node with children is a collapsible row: parents deep in the
 * tree (a department's own sub-pages) stay closed until tapped, so the
 * drawer isn't a wall of links, while top-level sections (depth 0) open
 * by default so their immediate children — e.g. the department roots
 * under "Departments & Offices" — are visible at a glance.
 *
 * A parent that also has its own URL splits into two zones: the label
 * navigates, a separate chevron toggles. A parent with no real URL
 * (href "#") toggles from anywhere on the row. Toggle controls are
 * <button>s (not <a>), so MobileTopBar's "close drawer when a link is
 * tapped" handler leaves them alone — only real navigation dismisses
 * the drawer.
 */
export function DrawerNav({
  menu,
  language,
}: {
  menu: DrawerMenuNode[];
  language: "en" | "mm";
}) {
  return (
    <ul className="space-y-0.5">
      {menu.map((node) => (
        <DrawerRow key={node._id} node={node} language={language} depth={0} />
      ))}
    </ul>
  );
}

function DrawerRow({
  node,
  language,
  depth,
}: {
  node: DrawerMenuNode;
  language: "en" | "mm";
  depth: number;
}) {
  // Top-level sections open by default; deeper branches start collapsed.
  const [open, setOpen] = useState(depth === 0);
  const label = pickLang(node.title, language) || node.slug || "";
  const hasChildren = (node.children?.length ?? 0) > 0;
  const hasRealUrl = !!node.url && node.url !== "#";

  const labelCls =
    depth === 0
      ? "text-sm font-medium text-foreground"
      : "text-[13px] text-muted-foreground";

  // Leaf — plain link.
  if (!hasChildren) {
    return (
      <li>
        <Link
          href={node.url || "#"}
          target={node.openInNewTab ? "_blank" : undefined}
          rel={node.openInNewTab ? "noopener noreferrer" : undefined}
          className={`block rounded-md px-3 py-2.5 transition-colors hover:bg-muted/60 hover:text-foreground ${labelCls}`}
        >
          {label}
        </Link>
      </li>
    );
  }

  return (
    <li>
      <div className="flex items-stretch rounded-md hover:bg-muted/60">
        {hasRealUrl ? (
          <Link
            href={node.url!}
            target={node.openInNewTab ? "_blank" : undefined}
            rel={node.openInNewTab ? "noopener noreferrer" : undefined}
            className={`min-w-0 flex-1 truncate px-3 py-2.5 transition-colors hover:text-foreground ${labelCls}`}
          >
            {label}
          </Link>
        ) : (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className={`min-w-0 flex-1 truncate px-3 py-2.5 text-left transition-colors hover:text-foreground ${labelCls}`}
          >
            {label}
          </button>
        )}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Collapse" : "Expand"}
          aria-expanded={open}
          className="inline-flex w-11 shrink-0 items-center justify-center text-muted-foreground transition-colors hover:text-foreground"
        >
          <ChevronDown
            size={16}
            className={`transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
      </div>
      {open && (
        <ul className="ml-3 mb-1 mt-0.5 space-y-0.5 border-l border-border/60 pl-2">
          {node.children!.map((child) => (
            <DrawerRow
              key={child._id}
              node={child}
              language={language}
              depth={depth + 1}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

export default DrawerNav;
